import handleException from '@/common/utils/errors';

export interface GoogleToken {
  accessToken: string;
  refreshToken: string;
  expiryDate: number;
}

export const STORAGE_KEY = 'google-auth';
export const GOOGLE_SCOPES = {
  GMAIL: 'https://www.googleapis.com/auth/gmail.readonly',
  CALENDAR: 'https://www.googleapis.com/auth/calendar.events.readonly',
  EMAIL: 'email',
  PROFILE: 'profile',
} as const;

export const useGoogleScope = () => {
  const checkScope = async (requiredScope: string) => {
    try {
      const tokenRaw = localStorage.getItem(STORAGE_KEY);
      if (!tokenRaw) {
        return false;
      }

      let token: GoogleToken = JSON.parse(tokenRaw);

      // Check if token is expired
      if (token.expiryDate < Date.now()) {
        // Try to refresh the token
        try {
          await refreshGoogleToken(token.refreshToken);
          // Get the new token after refresh
          const newTokenRaw = localStorage.getItem(STORAGE_KEY);
          if (!newTokenRaw) return false;
          token = JSON.parse(newTokenRaw);
        } catch {
          return false;
        }
      }

      const response = await fetch(
        `https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${token.accessToken}`
      );

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.scope?.includes(requiredScope) || false;
    } catch (error) {
      handleException(error);
      return false;
    }
  };

  const getStoredToken = (): GoogleToken | null => {
    try {
      const tokenRaw = localStorage.getItem(STORAGE_KEY);
      if (!tokenRaw) return null;
      return JSON.parse(tokenRaw);
    } catch {
      return null;
    }
  };

  const refreshGoogleToken = async (refreshToken: string): Promise<string> => {
    try {
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          client_secret: import.meta.env.VITE_GOOGLE_CLIENT_SECRET,
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();

      // Store the new tokens
      const tokenData: GoogleToken = {
        accessToken: data.access_token,
        refreshToken: refreshToken, // Keep existing refresh token
        expiryDate: Date.now() + (data.expires_in * 1000),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokenData));
      return data.access_token;
    } catch (error) {
      handleException(error);
      throw new Error('Failed to refresh Google token');
    }
  };

  const initiateGoogleAuth = (scopes: string[]) => {
    // Create and store a random state value
    const state = Math.random().toString(36).substring(7);
    localStorage.setItem('oauth_state', state);

    // Construct the Google OAuth URL
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.append('client_id', import.meta.env.VITE_GOOGLE_CLIENT_ID);
    authUrl.searchParams.append('redirect_uri', `${window.location.origin}/google/auth/callback`);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('scope', scopes.join(' '));
    authUrl.searchParams.append('access_type', 'offline');
    authUrl.searchParams.append('state', state);
    authUrl.searchParams.append('prompt', 'consent');
    authUrl.searchParams.append('include_granted_scopes', 'false');

    // Redirect to Google OAuth
    window.location.href = authUrl.toString();
  };

  const setupAuthListener = (
    requiredScope: string,
    onScopeChange: (hasScope: boolean) => void
  ) => {
    const intervalId = setInterval(async () => {
      try {
        const token = getStoredToken();
        if (!token) {
          onScopeChange(false);
          return;
        }

        // Check if token is expired
        if (token.expiryDate < Date.now()) {
          try {
            await refreshGoogleToken(token.refreshToken);
          } catch {
            onScopeChange(false);
            return;
          }
        }

        const response = await fetch(
          `https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${token.accessToken}`
        );

        if (!response.ok) {
          onScopeChange(false);
          return;
        }

        const data = await response.json();
        onScopeChange(data.scope?.includes(requiredScope) || false);
      } catch (error) {
        handleException(error);
        onScopeChange(false);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(intervalId);
  };

  return {
    checkScope,
    setupAuthListener,
    getStoredToken,
    refreshGoogleToken,
    initiateGoogleAuth,
  };
};
